import { useEffect, useRef, useState } from 'react';
import { useInView } from '@/hooks/useScrollReveal.ts';

const DURATION = 2500;

/** Ease-out so the number decelerates into its final value. */
function eased(progress: number): number {
  return 1 - (1 - progress) ** 2;
}

/**
 * Counts up to `value` when scrolled into view.
 *
 * The rendered text starts at the FINAL value so the prerendered HTML and the
 * no-JS experience both show real numbers, and the element is already at its
 * final width — no layout shift when the animation ends. The original started
 * at "0%+" in markup, which meant a crawler or a JS-blocked visitor saw zeros.
 */
function Counter({
  value,
  suffix,
  label,
  delayClass,
}: {
  value: number;
  suffix: string;
  label: string;
  delayClass?: string;
}) {
  const [display, setDisplay] = useState(value);
  const frame = useRef<number | null>(null);

  const ref = useInView<HTMLDivElement>(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / DURATION, 1);
      setDisplay(Math.floor(value * eased(progress)));
      if (progress < 1) frame.current = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    frame.current = requestAnimationFrame(tick);
  });

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );

  return (
    <div ref={ref} className={`fade-in ${delayClass ?? ''}`.trim()}>
      <div className="stat__number">
        {display.toLocaleString()}
        {suffix}
      </div>
      <div className="stat__label">{label}</div>
    </div>
  );
}

/**
 * The "30–50%" range stat. num1 counts up alone, then the dash and second
 * number grow outward from center as `.stat__reveal` expands.
 */
function RangeCounter({
  from,
  to,
  suffix,
  label,
  delayClass,
}: {
  from: number;
  to: number;
  suffix: string;
  label: string;
  delayClass?: string;
}) {
  const [display, setDisplay] = useState(from);
  const [revealed, setRevealed] = useState(true);
  const revealRef = useRef<HTMLSpanElement | null>(null);
  const frame = useRef<number | null>(null);

  const ref = useInView<HTMLDivElement>(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    setRevealed(false);
    setDisplay(0);
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / 2000, 1);
      setDisplay(Math.floor(from * eased(progress)));
      if (progress < 1) {
        frame.current = requestAnimationFrame(tick);
      } else {
        setDisplay(from);
        setRevealed(true);
      }
    };
    frame.current = requestAnimationFrame(tick);
  });

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );

  // Drive max-width off the measured content so the reveal transition works.
  const maxWidth = revealed
    ? `${revealRef.current?.scrollWidth ?? 200}px`
    : '0px';

  return (
    <div ref={ref} className={`fade-in ${delayClass ?? ''}`.trim()}>
      <div className="stat__number stat__number--range">
        <span>{display}</span>
        <span className="stat__reveal" style={{ maxWidth }} ref={revealRef}>
          <span>&ndash;</span>
          <span>
            {to}
            {suffix}
          </span>
        </span>
      </div>
      <div className="stat__label">{label}</div>
    </div>
  );
}

export function StatBar() {
  return (
    <div className="stat-bar">
      <div className="container--wide">
        <div className="stat-bar__grid">
          <Counter value={40} suffix="%+" label="Avg Cash Savings" />
          <RangeCounter
            from={30}
            to={50}
            suffix="%"
            label="Investment Tax Credit"
            delayClass="fade-in-delay-1"
          />
          <Counter
            value={200}
            suffix="+"
            label="Advisors Partnered With"
            delayClass="fade-in-delay-2"
          />
        </div>
      </div>
    </div>
  );
}
